package co.maytes.standupbot.dashboard.pages.app.standups

import co.maytes.standupbot.dashboard.styles.Layout
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import co.maytes.standupbot.dashboard.components.feedback.ApiErrorView
import co.maytes.standupbot.dashboard.components.feedback.ConfirmModal
import co.maytes.standupbot.dashboard.components.feedback.ConfirmTone
import co.maytes.standupbot.dashboard.components.feedback.SkeletonBox
import co.maytes.standupbot.dashboard.components.feedback.SkeletonCard
import co.maytes.standupbot.dashboard.components.feedback.SkeletonFormFields
import co.maytes.standupbot.dashboard.components.feedback.SkeletonLine
import co.maytes.standupbot.dashboard.components.feedback.SkeletonList
import co.maytes.standupbot.dashboard.components.feedback.SkeletonStatRow
import co.maytes.standupbot.dashboard.components.layout.AppShell
import co.maytes.standupbot.dashboard.components.layout.AuthGuard
import co.maytes.standupbot.dashboard.components.motion.StateSwap
import co.maytes.standupbot.dashboard.components.widgets.BackBar
import co.maytes.standupbot.dashboard.features.standupdetail.StandupDetailContainer
import co.maytes.standupbot.dashboard.features.standupdetail.StandupDetailContract
import co.maytes.standupbot.dashboard.features.standupruns.rememberStandupRunsStore
import co.maytes.standupbot.dashboard.features.standups.TeamDetailTabsBar
import co.maytes.standupbot.dashboard.features.standups.TeamMembersTab
import co.maytes.standupbot.dashboard.features.standups.TeamTab
import co.maytes.standupbot.dashboard.features.standups.rememberStandupsStore
import co.maytes.standupbot.dashboard.pages.app.standups.sections.EmptyRosterBanner
import co.maytes.standupbot.dashboard.pages.app.standups.sections.OverviewTab
import co.maytes.standupbot.dashboard.pages.app.standups.sections.RecentRunsPanel
import co.maytes.standupbot.dashboard.pages.app.standups.sections.RunForDateModal
import co.maytes.standupbot.dashboard.pages.app.standups.sections.ScheduleTab
import co.maytes.standupbot.dashboard.pages.app.standups.sections.StatsStrip
import co.maytes.standupbot.dashboard.pages.app.standups.sections.TeamHeader
import co.maytes.standupbot.dashboard.stores.ToastKind
import co.maytes.standupbot.dashboard.stores.rememberAuthStore
import co.maytes.standupbot.dashboard.stores.rememberUiStore
import co.maytes.standupbot.dashboard.styles.Spacing
import co.maytes.standupbot.shared.dto.standup.StandupDto
import co.maytes.standupbot.shared.dto.standup.UpdateStandupRequestDto
import com.kttipay.mvi.compose.subscribe
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import org.jetbrains.compose.web.css.px

@Page("/app/standups/{id}")
@Composable
fun TeamDetailPage() = AuthGuard {
    AppShell {
        StandupDetailScreen()
    }
}

@Composable
private fun StandupDetailScreen() {
    val auth = rememberAuthStore()
    val ctx = rememberPageContext()
    val ui = rememberUiStore()
    val standupsStore = rememberStandupsStore()
    val runsStore = rememberStandupRunsStore()
    val scope = rememberCoroutineScope()

    val standupId = ctx.route.params["id"] ?: ""
    val workspaceId = auth.currentUser?.workspace?.id

    val container = remember(standupsStore, runsStore) {
        StandupDetailContainer(scope, standupsStore, runsStore)
    }

    val state by container.subscribe { action ->
        when (action) {
            is StandupDetailContract.Action.Navigate -> ctx.router.navigateTo(action.path)
            is StandupDetailContract.Action.Toast -> ui.showToast(
                action.message,
                if (action.error) ToastKind.Error else ToastKind.Success,
            )
        }
    }

    LaunchedEffect(workspaceId, standupId) {
        if (!workspaceId.isNullOrBlank() && standupId.isNotBlank()) {
            container.intent(StandupDetailContract.Intent.Load(workspaceId, standupId))
        }
    }

    Box(
        modifier = Modifier
            .fillMaxWidth()
            .maxWidth(Layout.wide)
            .styleModifier { property("margin", "0 auto") },
    ) {
        Column(modifier = Modifier.fillMaxWidth()) {
            BackBar(fallbackPath = "/app/standups")

            StateSwap(state = state.phase) {
                when (val status = state.status) {
                    StandupDetailContract.Status.Loading -> DetailSkeleton()
                    is StandupDetailContract.Status.Error -> ApiErrorView(
                        error = status.error,
                        resourceLabel = "standup",
                        onRetry = { container.intent(StandupDetailContract.Intent.Retry) },
                        onBack = { container.intent(StandupDetailContract.Intent.Back) },
                    )
                    StandupDetailContract.Status.Ready -> {
                        val standup = state.standup
                        if (standup != null) {
                            Column(modifier = Modifier.fillMaxWidth()) {
                                StandupDetailBody(
                                    state = state,
                                    standup = standup,
                                    workspaceId = workspaceId,
                                    standupsStore = standupsStore,
                                    dispatch = { container.intent(it) },
                                    onViewRun = { runId -> ctx.router.navigateTo("/app/runs/$runId") },
                                )
                            }
                        } else {
                            DetailSkeleton()
                        }
                    }
                }
            }
        }
    }

    if (state.deleteOpen && state.standup != null) {
        val target = state.standup!!
        ConfirmModal(
            title = "Delete standup",
            body = "This stops the scheduled run and removes members from the roster. Past runs remain visible. Type the standup name to confirm.",
            confirmLabel = "Delete standup",
            tone = ConfirmTone.Danger,
            confirmMatch = target.name,
            onConfirm = { container.intent(StandupDetailContract.Intent.ConfirmDelete) },
            onDismiss = { container.intent(StandupDetailContract.Intent.CloseDelete) },
        )
    }
}

@Composable
private fun StandupDetailBody(
    state: StandupDetailContract.State,
    standup: StandupDto,
    workspaceId: String?,
    standupsStore: co.maytes.standupbot.dashboard.features.standups.StandupsStore,
    dispatch: (StandupDetailContract.Intent) -> Unit,
    onViewRun: (String) -> Unit,
) {
    TeamHeader(
        standup = standup,
        triggerInFlight = state.triggerInFlight,
        onTrigger = { dispatch(StandupDetailContract.Intent.Trigger(null)) },
        onRunForDate = { dispatch(StandupDetailContract.Intent.OpenRunForDate) },
        onDelete = { dispatch(StandupDetailContract.Intent.OpenDelete) },
    )

    Box(modifier = Modifier.fillMaxWidth().margin(top = Spacing.lg)) {
        StatsStrip(standup = standup)
    }

    val members = standupsStore.cachedMembers(standup.id)
    if (members != null && members.isEmpty()) {
        EmptyRosterBanner(onAddMembers = {
            dispatch(StandupDetailContract.Intent.SelectTab(TeamTab.Members))
        })
    }

    if (state.runForDateOpen) {
        RunForDateModal(
            teamTimezoneId = standup.timezone,
            onConfirm = { selected -> dispatch(StandupDetailContract.Intent.Trigger(selected)) },
            onDismiss = { dispatch(StandupDetailContract.Intent.CloseRunForDate) },
        )
    }

    Box(modifier = Modifier.fillMaxWidth().margin(top = Spacing.lg)) {
        TeamDetailTabsBar(
            active = state.activeTab,
            onSelect = { dispatch(StandupDetailContract.Intent.SelectTab(it)) },
        )
    }

    Box(modifier = Modifier.fillMaxWidth().margin(top = Spacing.lg)) {
        when (state.activeTab) {
            TeamTab.Overview -> workspaceId?.let { wid ->
                OverviewTab(
                    standup = standup,
                    workspaceId = wid,
                    inFlight = state.saveInFlight,
                    onSave = { dispatch(StandupDetailContract.Intent.Save(it)) },
                )
            }
            TeamTab.Schedule -> ScheduleTab(
                standup = standup,
                workspaceId = workspaceId,
                inFlight = state.saveInFlight,
                onSave = { spec ->
                    dispatch(
                        StandupDetailContract.Intent.Save(
                            UpdateStandupRequestDto(
                                scheduleCron = spec.toCron(),
                                timezone = spec.timezone,
                            ),
                        ),
                    )
                },
                onSaveHolidays = { req ->
                    dispatch(StandupDetailContract.Intent.Save(req))
                },
            )
            TeamTab.Members -> TeamMembersTab(
                store = standupsStore,
                workspaceId = workspaceId,
                standupId = standup.id,
            )
            TeamTab.Template -> workspaceId?.let { wid ->
                co.maytes.standupbot.dashboard.features.standups.TemplateTab(
                    standup = standup,
                    workspaceId = wid,
                    teamsStore = standupsStore,
                )
            }
        }
    }

    Box(modifier = Modifier.fillMaxWidth().margin(top = Spacing.xl)) {
        RecentRunsPanel(
            standup = standup,
            runs = state.recentRuns,
            onViewRun = onViewRun,
        )
    }
}

@Composable
private fun DetailSkeleton() {
    Column(modifier = Modifier.fillMaxWidth()) {
        SkeletonCard {
            com.varabyte.kobweb.compose.foundation.layout.Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = com.varabyte.kobweb.compose.ui.Alignment.CenterVertically,
                horizontalArrangement = com.varabyte.kobweb.compose.foundation.layout.Arrangement.SpaceBetween,
            ) {
                Column(modifier = Modifier.styleModifier { property("gap", "10px"); property("min-width", "0") }) {
                    SkeletonLine(widthPct = 46, heightPx = 22)
                    SkeletonLine(widthPct = 28, heightPx = 12)
                }
                com.varabyte.kobweb.compose.foundation.layout.Row(
                    modifier = Modifier.styleModifier { property("gap", "10px") },
                ) {
                    SkeletonBox(widthPx = 96, heightPx = 36, radiusPx = 8)
                    SkeletonBox(widthPx = 36, heightPx = 36, radiusPx = 8)
                }
            }
        }
        Box(modifier = Modifier.fillMaxWidth().margin(top = Spacing.lg)) {
            SkeletonStatRow(3)
        }
        Box(modifier = Modifier.fillMaxWidth().margin(top = Spacing.lg)) {
            SkeletonCard {
                SkeletonFormFields(4)
            }
        }
        Box(modifier = Modifier.fillMaxWidth().margin(top = Spacing.xl)) {
            SkeletonList(rows = 3)
        }
    }
}

private const val PAGE_MAX_WIDTH = 1000
